import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    linkedSignal,
    signal,
    ViewEncapsulation,
} from "@angular/core";
import { SEEventDetailsStore } from "../event/se.event.details.store";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { S25Util } from "../../../util/s25-util";
import { Bind } from "../../../decorators/bind.decorator";
import { S25ToggleButtonModule } from "../../s25-toggle-button/s25.toggle.button.module";
import { S25DatepickerModule } from "../../s25-datepicker/s25.datepicker.module";
import { S25DropdownPaginatedModule } from "../../s25-dropdown/s25.dropdown.paginated.module";
import { FormsModule } from "@angular/forms";
import { S25MultiselectModule } from "../../s25-multiselect/s25-multiselect.module";
import { s25Resource } from "../../../util/s25.resource";
import { SetRegistrationConfig } from "./attendance.service";
import { DataIngestionService } from "../../data-ingestion/data.ingestion.service";
import { NameService } from "../../../services/name.service";
import { SeOrgSecurityStore } from "../se.org.security.store";
import { SeAttendanceStore } from "./se.attendance.store";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import { S25Const } from "../../../util/s25-const";
import { S25EditableModule } from "../../s25-editable/s25.editable.module";

@Component({
    standalone: true,
    selector: "s25-ng-manage-registration-config",
    template: `
        @let conf = config();
        @if (!conf) {
            @if (readonly()) {
                No registration configured for this event
            } @else {
                <s25-ng-button [type]="'primary'" [onClick]="onConfigureRegistration">Add Registration</s25-ng-button>
            }
        } @else {
            <label class="form-row">
                Registration Open
                <s25-ng-editable-date-time
                    [(value)]="conf.start"
                    [disabled]="readonly()"
                    [dateInputId]="'registration-open'"
                />
            </label>
            <label class="form-row">
                Registration Close
                <s25-ng-editable-date-time
                    [(value)]="conf.end"
                    [disabled]="readonly()"
                    [dateInputId]="'registration-close'"
                />
            </label>
            <label class="form-row">
                Registration Form
                <s25-ingestion-point-dropdown
                    [value]="{ itemId: conf.formId }"
                    (valueChange)="conf.formId = $any($safeNavigationMigration($event?.itemId))"
                    [readOnly]="readonly()"
                    [useFilter]="'registration'"
                    [authlessOnly]="conf.registrantType === 'everyone'"
                    [hasNoneItem]="true"
                    [subCat]="$safeNavigationMigration(orgSecStore.orgId()?.toString())"
                />
            </label>

            <div class="form-row">
                <label for="who-can-register">Who Can Register</label>
                <div class="registrants">
                    <select
                        id="who-can-register"
                        class="cn-form__control"
                        [(ngModel)]="conf.registrantType"
                        (ngModelChange)="conf.registrants = []"
                        [disabled]="readonly()"
                    >
                        <option [ngValue]="'everyone'">Anyone</option>
                        <option [ngValue]="'users'">Any User</option>
                        <option [ngValue]="'contacts'">Select Contacts</option>
                        <option [ngValue]="'orgRoles'">Select Organization Roles</option>
                    </select>
                    @if (conf.registrantType === "contacts") {
                        <s25-ng-multiselect-search-criteria
                            [type]="'contacts'"
                            [selectedItems]="conf.registrants || []"
                            [showResults]="true"
                            [disabled]="readonly()"
                        />
                    } @else if (conf.registrantType === "orgRoles") {
                        <s25-ng-multiselect-search-criteria
                            [type]="'organizationRoles'"
                            [selectedItems]="conf.registrants || []"
                            [showResults]="true"
                            [disabled]="readonly()"
                        />
                    }
                    @if (conf.registrantType === "everyone") {
                        <p class="authless-help">
                            Anyone can register without signing in. A registration form is optional. If you use one, it
                            must have <strong>Allow authless submissions</strong> enabled in form settings.
                        </p>
                    }
                </div>
            </div>
            @if (!readonly()) {
                <div class="buttons">
                    <s25-ng-button class="button save" [type]="'primary'" [onClick]="onSave">
                        @if (saving() === "saved") {
                            <div class="fade"><s25-ng-icon [type]="'check'" [ariaLabel]="false" /> Saved</div>
                        } @else if (saving() === "error") {
                            <div class="fade"><s25-ng-icon [type]="'close'" [ariaLabel]="false" /> Error</div>
                        } @else {
                            Save
                        }
                    </s25-ng-button>
                    <s25-ng-button class="button delete" [type]="'danger--outline'" [onClick]="onDelete">
                        @if (deleting() === "error") {
                            <div class="fade"><s25-ng-icon [type]="'close'" [ariaLabel]="false" /> Error</div>
                        } @else {
                            Delete
                        }
                    </s25-ng-button>
                </div>
            }
        }
    `,
    styles: `
        :host {
            display: grid;
            gap: 1.25rem;
            grid-template-columns: 9em 1fr;

            background: var(--color--surface-bg);
            border-radius: 16px;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
            border: 1px solid var(--color--border);
            padding: 1.5rem;
        }

        .form-row {
            gap: 0.5rem;
            display: grid;
            grid-template-columns: subgrid;
            grid-column: span 2;
            align-items: center;
        }

        .registrants {
            display: grid;
            gap: 0.5rem;
        }

        .authless-help {
            margin: 0;
            font-size: 0.9rem;
            color: var(--color--text-muted, #666);
        }

        @keyframes fade-out {
            from {
                opacity: 1;
            }
            to {
                opacity: 0;
            }
        }

        .buttons {
            grid-column: 1/-1;
            gap: 0.5rem;
            display: flex;

            .save {
                width: 11ch;
            }

            .fade {
                opacity: 1;
                animation: fade-out 2s ease-in-out forwards;
            }
        }

        @media (max-width: 768px) {
            :host {
                grid-template-columns: 1fr;
                padding: 1rem;
            }

            .form-row {
                grid-column: span 1;
                grid-template-columns: 1fr;
                gap: 0.25rem;
            }

            .form-row label {
                font-weight: 500;
            }

            .buttons {
                flex-wrap: wrap;
            }
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        S25ButtonComponent,
        S25ToggleButtonModule,
        S25DatepickerModule,
        S25DropdownPaginatedModule,
        FormsModule,
        S25MultiselectModule,
        S25IconComponent,
        S25EditableModule,
    ],
})
export class ManageRegistrationConfigComponent {
    readonly eventStore = inject(SEEventDetailsStore);
    readonly attendanceStore = inject(SeAttendanceStore);
    readonly orgSecStore = inject(SeOrgSecurityStore);
    readonly readonly = computed(() => !this.orgSecStore.canManageRegistration());
    readonly saving = signal<"saving" | "saved" | "error" | undefined>(undefined);
    readonly deleting = signal<"deleting" | "deleted" | "error" | undefined>(undefined);

    readonly registrantNames = s25Resource({
        params: () => this.attendanceStore.registrationConfig(),
        loader: async ({ params: config, abortSignal }) => {
            if (!config) return undefined;
            if (config.registrantType === "everyone") return new Map();
            if (config.registrantType === "users") return new Map();
            return NameService.getNameMap(
                config.registrantType,
                config.registrants.map((r) => r.objectId),
            );
        },
    });
    readonly config = linkedSignal(() => {
        const config = this.attendanceStore.registrationConfig();
        if (!config || !this.registrantNames.lastValue()) return;
        return {
            start: S25Util.date.parseDropTZ(config.start),
            end: S25Util.date.parseDropTZ(config.end),
            formId: config.formId,
            registrantType: config.registrantType,
            registrants: config.registrants.map((r) => ({
                ...r,
                itemId: r.objectId,
                itemName: this.registrantNames.value()?.get(r.objectId),
            })),
        };
    });

    @Bind
    onConfigureRegistration() {
        const start = this.eventStore.startDate() ?? new Date();
        const end = this.eventStore.endDate() ?? new Date();
        this.config.set({
            start: S25Util.date.clone(start),
            end: S25Util.date.clone(end),
            formId: undefined,
            registrantType: "users",
            registrants: [],
        });
    }

    private async validateEveryoneRegistrationForm(formId: number | undefined): Promise<string | undefined> {
        if (!formId) {
            return undefined;
        }
        const ingestionPoint = await DataIngestionService.getIngestionPoint(formId);
        if (!ingestionPoint?.use?.registration) {
            return "The selected form is not enabled for registration use.";
        }
        if (!ingestionPoint.authless) {
            return (
                "The selected form must allow authless submissions when Anyone can register. " +
                "Enable Allow authless submissions in form settings or choose a different form."
            );
        }
        return undefined;
    }

    @Bind
    async onSave() {
        this.saving.set("saving");
        const eventId = this.eventStore.eventId();
        const config = this.config();
        if (!eventId || !config) return;

        if (config.registrantType === "everyone") {
            const validationError = await this.validateEveryoneRegistrationForm(config.formId);
            if (validationError) {
                this.saving.set("error");
                alert(validationError);
                setTimeout(() => this.saving.set(undefined), 2 * S25Const.ms.sec);
                return;
            }
        }

        const payload: SetRegistrationConfig = {
            eventId: eventId,
            start: S25Util.date.toS25ISODateTimeStr(config.start)!,
            end: S25Util.date.toS25ISODateTimeStr(config.end)!,
            formId: config.formId,
            registrantType: config.registrantType,
            registrants: config.registrants.map((r) => ({
                eventId: eventId,
                objectId: r.itemId,
            })),
        };
        const [_, err] = await S25Util.Maybe(this.attendanceStore.setRegistrationConfig(payload));
        if (!!err) {
            this.saving.set("error");
            alert("Something went wrong");
        } else {
            this.saving.set("saved");
        }

        setTimeout(() => this.saving.set(undefined), 2 * S25Const.ms.sec);
    }

    @Bind
    async onDelete() {
        if (!this.attendanceStore.registrationConfig()) {
            this.config.set(undefined);
            return;
        }
        this.deleting.set("deleting");
        const [_, err] = await S25Util.Maybe(this.attendanceStore.deleteRegistrationConfig());
        if (!!err) {
            this.deleting.set("error");
            alert("Something went wrong");
        } else {
            this.deleting.set("deleted");
        }

        setTimeout(() => this.deleting.set(undefined), 2 * S25Const.ms.sec);
    }
}
